import { useCallback } from 'react'
import { useEditorContext } from '@/scenes/engine'
import { ObjectType } from '@/scenes/engine/common/constants'
import {
  INDEXED_IMAGE_ACTIONS,
  IndexedImageAction,
} from '@/utils/indexedImage'
import { StaticImage } from 'fabric/fabric-impl'
import {
  OpenImageSource,
  openImageSourceInEditor,
} from '@/utils/openImageSourceInEditor'

/** Pass this (instead of `null`) when opening the first BAZAART_IMAGE on the current scene. */
export const OPEN_EDITOR_IMAGE_FROM_CURRENT_PAGE = 'imageFromCurrentPage' as const

export type OpenInEditorSource = OpenImageSource | typeof OPEN_EDITOR_IMAGE_FROM_CURRENT_PAGE

function shouldUseCurrentPageImage(source: OpenInEditorSource | null | undefined): boolean {
  return source == null || source === OPEN_EDITOR_IMAGE_FROM_CURRENT_PAGE
}

export function useOpenInEditor() {
  const { editor } = useEditorContext()

  const openInEditor = useCallback(async (
    source?: OpenInEditorSource | null,
    action: IndexedImageAction = INDEXED_IMAGE_ACTIONS.OPEN_EDITOR_AI_FASHION_MODELS
  ) => {
    try {
      if (!shouldUseCurrentPageImage(source)) {
        await openImageSourceInEditor(source as OpenImageSource, action)
        return
      }

      const currentSceneId = editor?.handlers?.pagesHandler?.currentScene?.sceneId
      if (!currentSceneId) {
        console.warn('openInEditor skipped: no scene found')
        return
      }

      const object = editor.handlers.pagesHandler
        .getObjectsOnScene(currentSceneId)
        .find((sceneObject: any) => sceneObject.type === ObjectType.BAZAART_IMAGE) as unknown as StaticImage | undefined

      if (!object) {
        console.warn('openInEditor skipped: no image object found on scene')
        return
      }

      await openImageSourceInEditor(object.getSrc(), action)
    } catch (error) {
      console.error('Failed to cache image for editor:', error)
    }
  }, [editor])

  return { openInEditor }
}
